import get from 'lodash/get';
import * as $protobuf from 'protobufjs';

import config from 'Config';

type ProtobufTimestamp = {
  seconds?: string;
  nanos?: number;
};

export type ProtobufSchemaType =
  | typeof import('Config/protobuf/authgateway').tinder.services
  | typeof import('Config/protobuf/dynamicui').tinder.services
  | typeof import('Config/protobuf/keepalive').tinder.services
  | typeof import('Config/protobuf/mediaservice').tinder.services
  | typeof import('Config/protobuf/roomservice').tinder.services;

// @TODO, find a way to create union of real type definition
export type GenericProtobufMessage = {
  encode: (message: any, writer?: $protobuf.Writer) => $protobuf.Writer;
  decode: (reader: $protobuf.Reader | Uint8Array, length?: number) => any;
  toObject: (message: any, options: any) => { [k: string]: any };
};
/**
 * Async load the protobuf definitions and return the schema object
 *
 * @return {Object} protobuf schema object
 */
export async function getProtobufSchema(path: ProtobufMessagePath) {
  const schema = await getProtobufModule(path);
  const schemaObject = schema?.default;
  const servicePath = config.protobufServicesPath ? config.protobufServicesPath.concat(path) : path;
  const schemaResult = get(schemaObject, servicePath, null);
  return schemaResult;
}

/**
 * Flow types to check that Protobuf message is in a known module
 */
export type ProtobufModule =
  | 'authgateway'
  | 'dynamicui'
  | 'keepalive'
  | 'mediaservice'
  | 'roomservice';

// Tuple item [0] is module, item [1] is message name, someday we could generate flow types for message name.
export type ProtobufMessagePath = [ProtobufModule, ...string[]];
export type SchemaType = ReturnType<typeof getProtobufModule>['then'];
/**
 * Async load a protobuf module and return the schema object for the module
 *
 * @param {Array<string>} path
 * @return {?Object}
 */
async function getProtobufModule(path: ProtobufMessagePath) {
  switch (path[0]) {
    case 'authgateway':
      return import(
        'Config/protobuf/authgateway' /* webpackChunkName: "async-protobuf-schema-authgateway" */
      );
    case 'dynamicui':
      return import(
        'Config/protobuf/dynamicui' /* webpackChunkName: "async-protobuf-schema-dynamicui" */
      );
    case 'roomservice':
      return import(
        'Config/protobuf/roomservice' /* webpackChunkName: "async-protobuf-schema-roomservice" */
      );
    case 'keepalive':
      return import(
        'Config/protobuf/keepalive' /* webpackChunkName: "async-protobuf-schema-keepalive" */
      );
    case 'mediaservice':
      return import(
        'Config/protobuf/mediaservice' /* webpackChunkName: "async-protobuf-schema-mediaservice" */
      );
    default:
      return null;
  }
}

/**
 * Get the value of a schema prop
 *
 * @param {Object} schema - protobuf schema object
 * @param {string|Array<string>} property - property name or path
 * @return {any} value of property
 */
export function getValue(schema: any, property: string | string[], defaultReturn?: any) {
  const val = get(schema, property, defaultReturn);
  return val?.value ?? val;
}

/**
 * Converts a Unix timestamp to protobuf JSON
 *
 * Note: Seconds are returned as a string because the seconds in a protobuf timestamp are supposed
 * to be 64-bit integers, which we don't have. Also, the nanoseconds can be fairly inaccurate due to
 * rounding errors, so don't expect too much from them!
 *
 * @param  {number} timestamp - timestamp, in milliseconds
 * @return {Object} protobuf timestamp object
 */
export function encodeProtobufTimestamp(timestamp: number = Date.now()): ProtobufTimestamp {
  return {
    seconds: Math.floor(timestamp * 1e-3).toString(),
    nanos: Math.floor((timestamp % 1e3) * 1e6),
  };
}

/**
 * Converts a protobuf timestamp object to a Unix time in milliseconds
 *
 * @param  {Object} timestamp - protobuf.js timestamp to convert
 * @param  {string|number} timestamp.seconds - number of seconds
 * @param  {number} timestamp.nanos - number of nanoseconds
 * @return {number} timestamp, in milliseconds
 */
/* eslint-disable no-bitwise, no-mixed-operators */
export function parseProtobufTimestamp(timestamp?: ProtobufTimestamp | null) {
  const ts = timestamp || ({} as ProtobufTimestamp); // handle null timestamp
  const { seconds = '0', nanos = 0 } = ts;
  return +seconds * 1e3 + Math.round(nanos * 1e-6);
}

// This makes zero sense that we need to know implementation
// detail for protobuf, but it wont' work if we dont do it here
// IStringValue
export const transformToIStringValue = (
  value: any // eslint-disable-line
) => ({
  value,
});
